/**
 * Раздел «Дивизионы»: рейтинг по заполненности данных.
 *
 * Дивизионы упорядочены от лучшего к худшему — так сразу видно,
 * где данные в порядке, а где требуется внимание. Строка открывает
 * дивизион: его объекты и показатели.
 */
import { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { selectGrouped } from '../../store/slices/switchesSlice';
import {
  selectDivisions,
  selectCanManageDivisions,
  createDivision,
  deleteDivision,
} from '../../store/slices/divisionsSlice';
import { getAllDivisionStats } from '../../domain/divisions';
import { plural } from '../../domain/plural';
import PageHeader from '../../components/common/PageHeader';
import ui from '../../components/common/ui.module.css';
import styles from './DivisionsPage.module.css';

export default function DivisionsPage() {
  const grouped = useSelector(selectGrouped);
  const divisionMap = useSelector(selectDivisions);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const canManage = useSelector(selectCanManageDivisions);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState(null);

  async function handleCreate() {
    const name = newName.trim();
    if (!name) return;
    setError(null);
    try {
      await dispatch(createDivision(name)).unwrap();
      setNewName('');
    } catch (message) {
      setError(String(message));
    }
  }

  async function handleDelete(name, objectCount) {
    const warning =
      objectCount > 0
        ? `Удалить дивизион «${name}»?\n\nОбъектов в нём: ${objectCount}. ` +
          'Они останутся в учёте, но перестанут относиться к какому-либо дивизиону.'
        : `Удалить дивизион «${name}»?`;
    if (!window.confirm(warning)) return;
    try {
      await dispatch(deleteDivision(name)).unwrap();
    } catch (message) {
      setError(String(message));
    }
  }

  const stats = useMemo(() => getAllDivisionStats(divisionMap, grouped), [divisionMap, grouped]);

  const totalSwitches = stats.reduce((sum, s) => sum + s.total, 0);

  const addForm = canManage && (
    <form
      className={styles.addRow}
      onSubmit={(e) => {
        e.preventDefault();
        handleCreate();
      }}
    >
      <input
        className={ui.search}
        placeholder="Название нового дивизиона"
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        aria-label="Название нового дивизиона"
      />
      <button className={`${ui.button} ${ui.primary}`} type="submit" disabled={!newName.trim()}>
        Добавить дивизион
      </button>
    </form>
  );

  if (!stats.length) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Дивизионы"
          subtitle="Рейтинг по заполненности данных"
          actions={addForm}
        />
        {error && <div className={styles.error}>{error}</div>}
        <div className={styles.emptyBox}>
          <div className={styles.emptyTitle}>Дивизионов пока нет</div>
          <p className={styles.emptyText}>
            {canManage
              ? 'Заведите первый дивизион, затем назначайте объекты: откройте объект и выберите ' +
                'дивизион рядом с его названием.'
              : 'Дивизионы заводит разработчик. Пока их нет, рейтинг и зоны ответственности не действуют.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Дивизионы"
        subtitle="Рейтинг по заполненности данных: доля коммутаторов без замечаний"
        actions={addForm}
      />
      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.rankCol}>Место</th>
              <th>Дивизион</th>
              <th className={styles.barCol}>Заполненность</th>
              <th className={styles.num}>Объектов</th>
              <th className={styles.num}>Коммутаторов</th>
              <th className={styles.num}>С замечаниями</th>
              <th className={styles.num}>Без SN Lyra</th>
              {canManage && <th aria-label="Действия" />}
            </tr>
          </thead>
          <tbody>
            {stats.map((item, index) => {
              const rank = index + 1;
              const open = () => navigate(`/divisions/${encodeURIComponent(item.division)}`);
              const tone =
                rank <= 3 ? styles.rankTop : index >= stats.length - 3 ? styles.rankLow : '';
              return (
                <tr
                  key={item.division}
                  onClick={open}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') open();
                  }}
                >
                  <td className={styles.rankCol}>
                    <span className={`${styles.rank} ${tone}`}>{rank}</span>
                  </td>
                  <td className={styles.name}>{item.division}</td>
                  <td className={styles.barCol}>
                    <span className={styles.completion}>
                      <span className={styles.track}>
                        <span className={styles.fill} style={{ width: `${item.completion}%` }} />
                      </span>
                      <span className={styles.pct}>
                        {String(item.completion).replace('.', ',')} %
                      </span>
                    </span>
                    {/* На телефоне столбцы с числами скрыты — те же числа строкой */}
                    <span className={styles.meta}>
                      {item.mags.length} {plural(item.mags.length, 'объект', 'объекта', 'объектов')}
                      {' · '}
                      {item.total} {plural(item.total, 'коммутатор', 'коммутатора', 'коммутаторов')}
                      {' · '}
                      <span className={item.issues ? styles.warnValue : ''}>
                        {item.issues} с замечаниями
                      </span>
                      {' · '}
                      <span className={item.noLyra ? styles.warnValue : ''}>
                        {item.noLyra} без SN Lyra
                      </span>
                    </span>
                  </td>
                  <td className={styles.num}>{item.mags.length}</td>
                  <td className={styles.num}>
                    {item.total}
                    <span className={styles.share}>
                      {totalSwitches ? Math.round((item.total / totalSwitches) * 100) : 0} % сети
                    </span>
                  </td>
                  <td className={`${styles.num} ${item.issues ? styles.warnValue : ''}`}>
                    {item.issues}
                  </td>
                  <td className={`${styles.num} ${item.noLyra ? styles.warnValue : ''}`}>
                    {item.noLyra}
                  </td>
                  {canManage && (
                    <td className={styles.actionsCol}>
                      <button
                        className={styles.delete}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item.division, item.mags.length);
                        }}
                        title="Удалить дивизион"
                        aria-label={`Удалить дивизион ${item.division}`}
                      >
                        ×
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
